import { useCallback, useRef } from 'react'

import { PointArray } from '@editor/interfaces/attribute'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { calculateIndexInPolyline, splitPolylineAtIndex } from '@editor/services/Polyline'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { selectInspectionItems } from '@editor/store/selectors'
import * as Sentry from '@sentry/react'

import { usePolylineSelector } from '../../../store/selector'
import {
  PolylineData,
  addCompletedPolyline,
  deleteCompletedPolylines,
  updateCompletedPolyline,
  upsertEditedPolyline,
} from '../../store/polyline'

/**
 * Split a polyline at a given point.
 */
const splitPolyline = (points: PointArray[], point: PointArray): PointArray[][] => {
  // Ensure edgeIndex is within valid bounds for splitting
  const edgeIndex = calculateIndexInPolyline(points, point)
  if (edgeIndex < 0 || edgeIndex >= points.length)
    throw new Error(`Invalid split index for polyline; Index: ${edgeIndex}, Points length: ${points.length}.`)

  const split = splitPolylineAtIndex(points, edgeIndex)
  if (!split) throw new Error('Failed to split saved polyline.')

  return split
}

const useSplitPolyline = (savedPolylines: InspectionItem[]) => {
  // Store
  const dispatch = useAppDispatch()
  const inspectionItems = useAppSelector(selectInspectionItems)
  const completedPolylines = usePolylineSelector((state) => state.polyline.completedPolylines)
  const editedPolylines = usePolylineSelector((state) => state.polyline.editedPolylines)

  // Refs
  const hoveredPolylineIdRef = useRef<string>(undefined)

  /**
   * Split a completed polyline at a given point.
   * If the resulting polylines have less than 2 points, they will be removed.
   *
   * @param polyline The polyline to be split.
   * @param point The point at which to split the polyline.
   */
  const splitCompletedPolylines = useCallback(
    ({ id, inspectionItemId, points, width }: PolylineData, point: PointArray) => {
      const splitPolylines = splitPolyline(points, point)

      // Update the existing polyline with the first part
      // If there's not enough points, it will be removed
      if (splitPolylines[0].length >= 2) {
        dispatch(
          updateCompletedPolyline({
            id,
            inspectionItemId,
            points: splitPolylines[0],
            width,
          }),
        )
      } else {
        dispatch(deleteCompletedPolylines([id]))
      }

      // Add a new polyline for the second part
      if (splitPolylines[1].length >= 2) {
        dispatch(
          addCompletedPolyline({
            id: crypto.randomUUID(),
            inspectionItemId,
            points: splitPolylines[1],
            width,
          }),
        )
      }
    },
    [dispatch],
  )

  /**
   * Split a saved polyline at a given point.
   *
   * @param polylineItem The saved polyline item to be split.
   * @param point The point at which to split the polyline.
   */
  const splitSavedPolylines = useCallback(
    (polylineItem: InspectionItem, point: PointArray) => {
      if (!polylineItem.polyline_length?.positions_for_distance?.length)
        throw new Error('No positions found in saved polyline.')

      const splitPolylines = splitPolyline(polylineItem.polyline_length.positions_for_distance, point)

      // Try to find the width from edited polylines first, then fallback to inspection items if not found
      let polylineWidth = editedPolylines.find((pl) => pl.id === polylineItem.inspection_item_id)?.width
      if (polylineWidth === undefined) {
        polylineWidth =
          inspectionItems.items.find(
            (item) =>
              item.polyline_length_id === polylineItem.inspection_item_id &&
              item.item_type === InspectionItemType.PolylineWidth,
          )?.polyline_width?.estimated_value ?? undefined
      }

      // Find which one of the 2 is still valid to update data, defaulting to the first
      // eg: if the first is invalid but the second is valid, we use the second one to update
      const validPolylineIndex = splitPolylines.findIndex((pl) => pl.length >= 2)

      // If nothing else remains, mark it for deletion
      if (validPolylineIndex === -1) {
        dispatch(
          upsertEditedPolyline({
            id: polylineItem.inspection_item_id!,
            inspectionItemId: polylineItem.inspection_item_id!,
            points: [],
            isDeleted: true,
          }),
        )
        return
      }

      const validPolyline = splitPolylines[validPolylineIndex]
      const secondPolyline = splitPolylines[1 - validPolylineIndex]

      // Update the existing saved polyline with the first part
      if (validPolyline.length >= 2) {
        dispatch(
          upsertEditedPolyline({
            id: polylineItem.inspection_item_id!,
            inspectionItemId: polylineItem.inspection_item_id!,
            points: validPolyline,
            width: polylineWidth,
          }),
        )
      }

      // Add a new polyline for the second part
      if (secondPolyline.length >= 2) {
        dispatch(
          addCompletedPolyline({
            id: crypto.randomUUID(),
            points: secondPolyline,
            width: polylineWidth,
            inspectionItemId: polylineItem.inspection_item_id,
            groupId: polylineItem.inspection_group_id,
          }),
        )
      }
    },
    [inspectionItems, editedPolylines, dispatch],
  )

  /**
   * Handle splitting of a polyline at a given point.
   *
   * @param point The point at which to split the polyline.
   */
  const handleSplitPolyline = useCallback(
    (point: PointArray) => {
      if (!hoveredPolylineIdRef.current || !point) return

      try {
        // Try to find the polyline in completed polylines first
        const completedPolyline = completedPolylines.find((pl) => pl.id === hoveredPolylineIdRef.current)
        if (completedPolyline) {
          splitCompletedPolylines(completedPolyline, point)
          return
        }

        // If not found, try to find in saved polylines
        const savedPolylineItem = savedPolylines.find(
          (item) =>
            item.inspection_item_id === hoveredPolylineIdRef.current &&
            item.item_type === InspectionItemType.PolylineLength,
        )
        if (savedPolylineItem) {
          splitSavedPolylines(savedPolylineItem, point)
        }
      } catch (error) {
        Sentry.captureException(error)
      } finally {
        hoveredPolylineIdRef.current = undefined
      }
    },
    [completedPolylines, savedPolylines, splitCompletedPolylines, splitSavedPolylines],
  )

  return {
    handleSplitPolyline,
    hoveredPolylineIdRef,
  }
}

export default useSplitPolyline
